import { useEffect, useRef, useState } from "react";
import { DANGER, NAVY } from "../styles/palette";
import { sb } from "../lib/supabase";
import { loadStripe } from "../lib/stripe";
import { DEO_FREQ_OPTIONS, DEO_PER_VISIT, getDeoMonthly, getPrice, getTotalMonthly } from "../lib/pricing";
import { getDayForZip, getSecondDayForClient, getServiceDayForClient } from "../lib/serviceArea";
import { firstServiceDate } from "../lib/date";
import { geocodeAddress, GOOGLE_MAPS_KEY } from "../lib/maps";
import { ClickableDate } from "./ClickableDate";
import { formatPhoneInput, onlyDigits } from "../lib/format";
import { Icon } from "./Icon";
import { useIsMobile } from "../lib/useIsMobile";
import { localDateStr } from "../lib/date";

export function ClientModal({ client, prefill, onSave, onClose, showToast, clients, pricing, zipSecondDay, stripeAccountId }) {
  const isMobile = useIsMobile();
  // `prefill` only seeds the NEW-client form (e.g. converting a lead). It does
  // NOT change edit-vs-new mode, the starting step, or the onSave path — those
  // stay keyed on `client` only. So convert opens with client=null + prefill
  // => new-client mode, prefilled, create-customer runs on save.
  const [step,    setStep]    = useState(client ? 2 : 1); // 1=Info, 2=Service, 3=Payment
  const [form,    setForm]    = useState({
    first_name:  client?.first_name  ?? prefill?.first_name  ?? "",
    last_name:   client?.last_name   ?? prefill?.last_name   ?? "",
    email:       client?.email       ?? prefill?.email       ?? "",
    phone:       client?.phone       ?? (prefill?.phone ? formatPhoneInput(prefill.phone) : ""),
    // Second household number — gets the ETA and completion texts too (send-eta /
    // send-job-complete fan out to both, deduped).
    phone_secondary: client?.phone_secondary ?? "",
    address:     client?.address     ?? prefill?.address     ?? "",
    city:        client?.city        ?? prefill?.city        ?? "",
    state:       client?.state       ?? prefill?.state       ?? "",
    zip:         client?.zip         ?? prefill?.zip         ?? "",
    lat:         client?.lat         ?? prefill?.lat         ?? null,
    lng:         client?.lng         ?? prefill?.lng         ?? null,
    dogs:        client?.dogs        ?? prefill?.dogs        ?? 1,
    frequency:   client?.frequency   ?? prefill?.frequency   ?? "weekly",
    notes:       client?.notes       ?? "",
    // Was being SENT on save but never seeded, so it was always undefined and the
    // field silently never saved from this modal.
    visit_notes: client?.visit_notes ?? "",
    start_date:  client?.start_date  ?? localDateStr(),
    deo_enabled: client?.deo_enabled ?? prefill?.deo_enabled ?? false,
    deo_frequency: client?.deo_frequency ?? prefill?.deo_frequency ?? "biweekly",
    // "" = let the backend derive the fortnight from the start date (the honest default).
    biweekly_parity: client?.biweekly_parity ?? "",
    sms_consent: client?.sms_consent ?? prefill?.sms_consent ?? false,
  });
  const [saving,       setSaving]      = useState(false);
  const [skipCard,     setSkipCard]     = useState(false);
  const [cardError,    setCardError]    = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [stripeReady,  setStripeReady]  = useState(false);
  const stripeRef   = useRef(null);
  const elementsRef = useRef(null);
  const cardElRef   = useRef(null);
  const mountedRef  = useRef(false);
  const addressRef   = useRef(null);
  const acSessionRef = useRef(null);   // Places autocomplete session token (billing)
  const justPickedRef = useRef(false); // skip the refetch right after a selection
  const [addrSuggestions, setAddrSuggestions] = useState([]);

  // Mount Stripe card element on step 3 (only if not skipping)
  useEffect(()=>{
    if (step!==3||client||skipCard||mountedRef.current) return;
    mountedRef.current = true;
    loadStripe(stripeAccountId).then(stripe=>{
      stripeRef.current   = stripe;
      elementsRef.current = stripe.elements();
      const card = elementsRef.current.create("card",{
        // 16px on phones: the card iframe is an input too — under 16px iOS zooms the page on
        // focus and never zooms back (the global 16px input rule can't reach inside the iframe).
        style:{ base:{ fontFamily:"'DM Sans', sans-serif", fontSize:isMobile?"16px":"14px", color:"#1a1a2e", "::placeholder":{color:"#a0aec0"} }, invalid:{ color:DANGER } },
        hidePostalCode: true,
      });
      card.mount("#stripe-card-element");
      card.on("change", e=>{ setCardError(e.error?.message||""); setCardComplete(e.complete); });
      cardElRef.current = card;
      setStripeReady(true);
    }).catch(()=>showToast("Could not load Stripe","error"));
    return ()=>{ if(cardElRef.current){ cardElRef.current.destroy(); mountedRef.current=false; } };
  },[step, skipCard]); // eslint-disable-line react-hooks/exhaustive-deps

  // Address autocomplete via Places API (New) REST + our own dropdown. The legacy
  // google.maps.places.Autocomplete widget is no longer available to Google Maps
  // accounts created after Mar 2025, so we drive it ourselves. Debounced lookup on
  // what they type; fails silently (no dropdown) if Places API (New) isn't enabled
  // — the geocode-on-save fallback still records lat/lng either way.
  useEffect(()=>{
    if (client || step!==1) return;
    if (justPickedRef.current) { justPickedRef.current = false; setAddrSuggestions([]); return; }
    const q = (form.address||"").trim();
    if (q.length < 4 || !GOOGLE_MAPS_KEY) { setAddrSuggestions([]); return; }
    if (!acSessionRef.current) acSessionRef.current = (window.crypto?.randomUUID?.() || String(Math.random()).slice(2));
    const t = setTimeout(async ()=>{
      try {
        const r = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
          method:"POST",
          headers:{ "Content-Type":"application/json", "X-Goog-Api-Key": GOOGLE_MAPS_KEY },
          body: JSON.stringify({ input:q, includedRegionCodes:["us"], sessionToken: acSessionRef.current }),
        });
        const d = await r.json();
        setAddrSuggestions(Array.isArray(d.suggestions) ? d.suggestions.map(s=>s.placePrediction).filter(Boolean).slice(0,5) : []);
      } catch { setAddrSuggestions([]); }
    }, 250);
    return ()=>clearTimeout(t);
  },[form.address, client, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selecting a suggestion → fetch its details for structured address + coords.
  async function pickSuggestion(p) {
    try {
      const r = await fetch(`https://places.googleapis.com/v1/places/${p.placeId}?sessionToken=${acSessionRef.current||""}`, {
        headers:{ "X-Goog-Api-Key": GOOGLE_MAPS_KEY, "X-Goog-FieldMask":"addressComponents,location,formattedAddress" },
      });
      const place = await r.json();
      const C = {}; (place.addressComponents||[]).forEach(c=>(c.types||[]).forEach(t=>{ C[t]=c; }));
      const g = (k,which)=> C[k] ? (which==="short" ? C[k].shortText : C[k].longText) : "";
      const street = [g("street_number"), g("route")].filter(Boolean).join(" ");
      const city   = g("locality") || g("sublocality") || g("postal_town") || "";
      const state  = g("administrative_area_level_1","short") || "";
      const zip    = g("postal_code") || "";
      justPickedRef.current = true;
      acSessionRef.current  = null; // session token is consumed by a details fetch
      setForm(pp=>({ ...pp,
        address: street || place.formattedAddress || pp.address,
        ...(city?{city}:{}), ...(state?{state}:{}), ...(zip?{zip}:{}),
        lat: place.location?.latitude ?? null, lng: place.location?.longitude ?? null,
      }));
      setAddrSuggestions([]);
    } catch { setAddrSuggestions([]); }
  }

  const price      = getPrice(form.frequency, form.dogs, pricing);
  // Editing an existing client: show THEIR day (a per-client override beats the zip).
  // Adding a new one: there is no client yet, so the zip is all we have.
  const serviceDay = client
    ? (getServiceDayForClient({ ...client, zip: form.zip }) || "—")
    : (form.zip ? getDayForZip(form.zip) : "—");
  // The date the generator will ACTUALLY schedule first. A biweekly client added on
  // the far side of their fortnight can otherwise wait up to 13 days, and nothing on
  // this form said so — you'd only find out when they didn't appear on the board.
  // (Kevin Kirby, added Mon 2026-07-27, first visit Tue 2026-08-04.)
  const firstVisit = firstServiceDate(
    form.start_date,
    serviceDay && serviceDay !== "—" ? serviceDay : null,
    form.frequency,
    getSecondDayForClient({ ...(client || {}), zip: form.zip }, zipSecondDay || {}),
    form.biweekly_parity || null,
  );
  const firstVisitLabel = firstVisit
    ? new Date(firstVisit + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
    : null;
  const daysAway = firstVisit
    ? Math.round((new Date(firstVisit + "T12:00:00") - new Date(new Date().toDateString())) / 864e5)
    : null;
  const f          = k => e => setForm(p=>({...p,[k]:e.target.value}));



  async function handleSubmit() {
    setSaving(true);
    const fullName = `${form.first_name||""} ${form.last_name||""}`.trim();
    try {
      // Coordinates: on a new client or a CHANGED address, geocode via Google so we
      // store rooftop coords + confidence and stamp location_source='google' — the
      // rooftop backfill uses that to tell app-set pins from Census and to NEVER
      // re-touch a 'manual' pin. An UNCHANGED address on edit is left exactly as-is.
      let lat = form.lat, lng = form.lng;
      let location_source = client?.location_source ?? null;
      let location_type   = client?.location_type ?? null;
      const addrChanged = !client || form.address !== (client.address || "") || (form.zip || "") !== (client.zip || "");
      if (addrChanged && form.address && form.zip) {
        try {
          const g = await geocodeAddress(form.address, form.zip);
          if (g) { lat = g.lat; lng = g.lng; location_source = "google"; location_type = g.location_type || null; }
          else if (lat != null && lng != null) { location_source = "google"; location_type = null; } // Places coords only
        } catch { if (lat != null && lng != null) location_source = "google"; }
      }
      if (!client) {
        const dupEmail   = form.email   ? clients.find(c=>c.email?.toLowerCase()===form.email.toLowerCase()) : null;
        const dupAddress = form.address ? clients.find(c=>c.address?.toLowerCase()===form.address.toLowerCase()&&c.zip===form.zip) : null;
        if (dupEmail) {
          if (!window.confirm(`A client with email "${form.email}" already exists (${dupEmail.name}). Add anyway?`)) { setSaving(false); return; }
        } else if (dupAddress) {
          if (!window.confirm(`A client at "${form.address}, ${form.zip}" already exists (${dupAddress.name}). Add anyway?`)) { setSaving(false); return; }
        }

        let paymentMethodId = null, pmLast4 = null, pmBrand = null;

        // If card is being added now, tokenize it
        if (!skipCard && cardComplete) {
          const { paymentMethod, error } = await stripeRef.current.createPaymentMethod({
            type: "card", card: cardElRef.current,
            billing_details: { name: fullName, email: form.email },
          });
          if (error) { setCardError(error.message); setSaving(false); return; }
          paymentMethodId = paymentMethod.id;
          pmLast4         = paymentMethod.card.last4;
          pmBrand         = paymentMethod.card.brand;
        }

        const res = await sb.fn("create-customer", {
          first_name:       form.first_name,
          last_name:        form.last_name,
          email:            form.email,
          phone:            form.phone,
          phone_secondary:  form.phone_secondary || null,
          address:          form.address,
          city:             form.city,
          state:            form.state,
          zip:              form.zip,
          dogs:             Number(form.dogs),
          frequency:        form.frequency,
          notes:            form.notes,
          visit_notes:      form.visit_notes,
          start_date:       form.start_date,
          deo_enabled:      form.deo_enabled,
          deo_frequency:    form.deo_frequency,
          biweekly_parity:  form.biweekly_parity || null,
          referred_by:      form.referred_by,
          sms_consent:      form.sms_consent,
          technician_id:    form.technician_id,
          technician_name:  form.technician_name,
          payment_method_id: paymentMethodId,
          pm_last4:          pmLast4,
          pm_brand:          pmBrand,
          lat,
          lng,
          location_source,
          location_type,
        });
        if (res.error) { showToast(res.error,"error"); setSaving(false); return; }
        showToast(paymentMethodId ? `${fullName} added and activated!` : `${fullName} added — send payment link to collect card`);
        onSave(res.client || { ...form, dogs:Number(form.dogs) });
      } else {
        onSave({ ...form, dogs:Number(form.dogs), lat, lng, location_source, location_type });
      }
    } catch(e) { showToast("Unexpected error — please try again","error"); }
    setSaving(false);
  }

  const steps = client ? null : (
    <div className="steps">
      {[["1","Contact Info"],["2","Service Plan"],["3","Payment"]].map(([num,label],i)=>(
        <div key={num} className="step">
          <div className={`step-dot${step>i+1?" done":step===i+1?" active":""}`}>{step>i+1?"✓":num}</div>
          <div className={`step-label${step===i+1?" active":""}`}>{label}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>{client?"Edit Client":"New Client Onboarding"}</h3>
          <button className="close-btn" onClick={onClose} aria-label="Close"
            style={isMobile?{minWidth:40,minHeight:40,display:"inline-flex",alignItems:"center",justifyContent:"center",margin:"-8px -10px -8px 0"}:undefined}>×</button>
        </div>
        <div className="modal-body">
          {steps}

          {/* STEP 1 — Contact Info */}
          {(step===1||client) && (
            <div className="form-grid" style={isMobile?{gridTemplateColumns:"1fr"}:undefined}>
              <div className="form-group"><label>First Name *</label><input value={form.first_name} onChange={f("first_name")} required placeholder="Jane" autoComplete="given-name"/></div>
              <div className="form-group"><label>Last Name *</label><input value={form.last_name} onChange={f("last_name")} required placeholder="Smith" autoComplete="family-name"/></div>
              <div className="form-group"><label>Email *</label><input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" value={form.email} onChange={f("email")} required placeholder="jane@email.com"/></div>
              {/* inputMode=tel: the iPhone shows the number pad instead of the full QWERTY keyboard */}
              <div className="form-group"><label>Phone</label><input value={form.phone} inputMode="tel" autoComplete="tel" onChange={e=>setForm(p=>({...p,phone:formatPhoneInput(e.target.value)}))} placeholder="555-555-5555" maxLength={12}/></div>
              <div className="form-group">
                <label>Second Phone <span style={{fontWeight:400,color:"#8993a4",fontSize:11}}>— optional</span></label>
                <input value={form.phone_secondary} inputMode="tel" autoComplete="off" onChange={e=>setForm(p=>({...p,phone_secondary:formatPhoneInput(e.target.value)}))} placeholder="555-555-5555" maxLength={12}/>
                <div style={{fontSize:11,color:"#8993a4",marginTop:3}}>Also gets ETA and “service complete” texts.</div>
              </div>
              <div className="form-group full">
                <label style={{display:"flex",alignItems:"flex-start",gap:8,fontWeight:400,cursor:"pointer",margin:0}}>
                  <input type="checkbox" checked={form.sms_consent} onChange={e=>setForm(p=>({...p,sms_consent:e.target.checked}))} style={{marginTop:3,width:16,height:16,flexShrink:0,accentColor:NAVY}}/>
                  <span style={{fontSize:12.5,color:"#42526e",lineHeight:1.5}}>Client consents to service &amp; ETA text messages at this number. Only check if they've opted in — texts (ETA, job-complete) are never sent without it.</span>
                </label>
              </div>
              <div className="form-group"><label>Address</label>
                <div style={{position:"relative",width:"100%"}}>
                  <input ref={addressRef} value={form.address} onChange={f("address")} placeholder="Start typing the address…" autoComplete="off" style={{width:"100%"}}/>
                  {addrSuggestions.length>0 && (
                    // Phone: the list flows IN the form (not absolute) so the sheet grows and
                    // scrolls to it with the keyboard up, capped at 4; taps use onClick — the
                    // desktop onMouseDown relies on iOS's emulated mouse events, which are
                    // skipped when the finger moves during the tap.
                    <div style={isMobile
                      ? {position:"static",marginTop:4,background:"#fff",border:"1px solid #e3e8ef",borderRadius:8,boxShadow:"0 4px 14px rgba(0,0,0,.08)",overflow:"hidden"}
                      : {position:"absolute",top:"100%",left:0,right:0,zIndex:50,background:"#fff",border:"1px solid #e3e8ef",borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,.12)",marginTop:2,overflow:"hidden"}}>
                      {(isMobile ? addrSuggestions.slice(0,4) : addrSuggestions).map(p=>(
                        <div key={p.placeId}
                          onMouseDown={isMobile?undefined:()=>pickSuggestion(p)}
                          onClick={isMobile?()=>pickSuggestion(p):undefined}
                          style={{padding:isMobile?"11px 12px":"9px 12px",cursor:"pointer",fontSize:isMobile?14:13,borderBottom:"1px solid #f4f5f7",minHeight:isMobile?44:undefined}}>
                          <div style={{fontWeight:600,color:NAVY}}>{p.structuredFormat?.mainText?.text || p.text?.text}</div>
                          {p.structuredFormat?.secondaryText?.text && <div style={{fontSize:isMobile?12:11,color:"#8993a4",marginTop:1}}>{p.structuredFormat.secondaryText.text}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Zip Code *</label>
                <input value={form.zip} onChange={e=>setForm(p=>({...p,zip:onlyDigits(e.target.value,5)}))} required inputMode="numeric" maxLength={5} placeholder="10016"/>
                {form.zip && (serviceDay
                  ? <span style={{fontSize:11,color:"#8892b0",marginTop:3,display:"block"}}>📅 Service day: <strong style={{textTransform:"capitalize",color:NAVY}}>{serviceDay}</strong></span>
                  : <span style={{fontSize:11,color:"#bf2600",background:"#ffebe6",border:"1px solid #ffbdad",borderRadius:6,padding:"6px 10px",marginTop:4,display:"block",lineHeight:1.4}}>⚠️ Zip {form.zip} has no service day yet. Add it in <strong>Settings → Team &amp; Service → Service Area</strong> first — otherwise this client can’t be scheduled and saving will be blocked.</span>
                )}
              </div>
              <div className="form-group full"><label>Notes / Gate Code <span style={{fontWeight:400,color:"#8993a4",fontSize:11}}>— office only</span></label><textarea value={form.notes} onChange={f("notes")} placeholder="Gate code, dog names, backyard access..."/></div>
              <div className="form-group full">
                <label>Service Note <span style={{fontWeight:400,color:"#8993a4",fontSize:11}}>— shows on every job, techs see this</span></label>
                <textarea value={form.visit_notes} onChange={f("visit_notes")} placeholder="e.g. Check back gate. Dog is friendly but jumps. Leave gate closed."/>
              </div>
            </div>
          )}

          {/* STEP 2 — Service Plan. EDIT mode shows it READ-ONLY: plan & deodorizer
              changes alter Stripe and must go through the client's PROFILE
              (update-subscription) — the edit modal stays contact-only so it can't
              drift billing. NEW clients (add/convert) set their plan editably below. */}
          {client && (
            <div style={{marginTop:16}}>
              <div style={{fontFamily:"Syne",fontWeight:700,fontSize:13,color:NAVY,marginBottom:10,borderTop:"1px solid #f0f3fb",paddingTop:16}}>Service Plan</div>
              <div style={{fontSize:13,fontWeight:600,color:"#1a1f36",textTransform:"capitalize"}}>
                {form.frequency} · {form.dogs} dog{form.dogs>1?"s":""}{form.deo_enabled?` · + ${form.deo_frequency} deodorizer`:""}
              </div>
              <div style={{fontSize:11.5,color:"#42526e",marginTop:8,background:"#f5f6f8",border:"1px solid #e3e8ef",borderRadius:6,padding:"8px 11px"}}>
                To change the plan or deodorizer, open this client's <strong>profile</strong>.
              </div>
            </div>
          )}
          {!client && step===2 && (
            <div>
              <div className="form-grid" style={isMobile?{gridTemplateColumns:"1fr"}:undefined}>
                <div className="form-group">
                  <label>Number of Dogs</label>
                  <select value={form.dogs} onChange={f("dogs")}>
                    {[1,2,3,4,5].map(n=><option key={n} value={n}>{n} dog{n>1?"s":""}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Service Frequency</label>
                  <select value={form.frequency} onChange={f("frequency")}>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-Weekly (every other week)</option>
                    <option value="twice-weekly">Twice a Week</option>
                  </select>
                </div>
                {/* Which of the two alternating weeks. Defaults to deriving it from the start
                    date, which is right for almost every new client — override only when
                    slotting someone into an existing round. */}
                {!client && (form.frequency === "biweekly" || (form.deo_enabled && form.deo_frequency === "biweekly")) && (
                  <div className="form-group">
                    <label>Fortnight</label>
                    <select value={form.biweekly_parity} onChange={f("biweekly_parity")}>
                      <option value="">Auto — from their start date</option>
                      <option value="even">Even weeks</option>
                      <option value="odd">Odd weeks</option>
                    </select>
                  </div>
                )}
                <div className="form-group full">
                  <label>Service Start Date</label>
                  <ClickableDate value={form.start_date} onChange={v=>setForm(p=>({...p,start_date:v}))} min={localDateStr()}/>
                  {form.start_date && form.start_date > localDateStr() && (
                    <div style={{fontSize:11,color:"#974f0c",background:"#fffae6",padding:"4px 8px",borderRadius:4,marginTop:4}}>
                      ⏳ Future start date — card will be saved now. First charge begins when service starts.
                    </div>
                  )}
                </div>
                {/* What the generator will ACTUALLY schedule. Shown before saving so a
                    fortnight that lands on the far week is caught here, not on the board. */}
                {firstVisitLabel && (
                  <div className="form-group full">
                    <div style={{
                      fontSize:12, padding:"7px 10px", borderRadius:6, marginTop:2,
                      background: daysAway>7 ? "#fffae6" : "#f0f7ff",
                      color:      daysAway>7 ? "#974f0c" : "#0b4a8f",
                      border:`1px solid ${daysAway>7 ? "#ffe0a3" : "#cfe3fb"}`,
                    }}>
                      📅 First visit: <strong>{firstVisitLabel}</strong>
                      {daysAway>7 && (
                        <> — {daysAway} days out, because their fortnight falls on the opposite week.
                        {!client && form.frequency==="biweekly" ? <> Set <strong>Fortnight</strong> above to serve them sooner.</> : null}</>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Deodorizer add-on */}
              <div style={{background:"#f5f6f8",borderRadius:8,padding:14,marginTop:12}}>
                <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",marginBottom:0}}>
                  <input type="checkbox" checked={form.deo_enabled}
                    onChange={e=>setForm(p=>({...p,deo_enabled:e.target.checked}))}
                    style={{accentColor:NAVY,width:16,height:16,flexShrink:0}}/>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:"#1a1f36"}}>💨 Add Deodorizer Service</div>
                    <div style={{fontSize:11,color:"#8993a4",marginTop:2}}>Recurring deodorizer treatment on their service day</div>
                  </div>
                </label>
                {form.deo_enabled && (
                  <div style={{marginTop:12}}>
                    <label style={{fontSize:12,fontWeight:500,color:"#42526e",display:"block",marginBottom:5}}>Deodorizer Frequency</label>
                    <select value={form.deo_frequency} onChange={e=>setForm(p=>({...p,deo_frequency:e.target.value}))}>
                      {(DEO_FREQ_OPTIONS[form.frequency]||["biweekly","monthly"]).map(f=>(
                        <option key={f} value={f}>{f==="weekly"?"Weekly":f==="biweekly"?"Bi-Weekly":"Monthly"}</option>
                      ))}
                    </select>
                    <div style={{fontSize:12,color:"#006644",background:"#e3fcef",padding:"6px 10px",borderRadius:6,marginTop:6,fontWeight:500}}>
                      ${(pricing?.deoPerVisit?.[form.deo_frequency] ?? DEO_PER_VISIT[form.deo_frequency])} per visit · ${getDeoMonthly(form.deo_frequency, pricing).toFixed(2)}/month
                    </div>
                  </div>
                )}
              </div>

              <div className="price-tag" style={{marginTop:14}}>
                ${getTotalMonthly(form.frequency,Number(form.dogs),form.deo_enabled,form.deo_frequency,pricing).toFixed(2)}<span> / month</span>
                <div style={{fontSize:11,color:"#8892b0",marginTop:4,fontFamily:"DM Sans",fontWeight:400}}>
                  {form.dogs} dog{form.dogs>1?"s":""} · {form.frequency} cleanup{form.deo_enabled?` + ${form.deo_frequency==="weekly"?"Weekly":form.deo_frequency==="biweekly"?"Bi-Weekly":"Monthly"} Deodorizer`:""}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 — Payment */}
          {step===3&&!client&&(
            <div>
              {/* Skip checkbox */}
              <div style={{marginBottom:16,padding:"10px 14px",background:"#f8f9fb",borderRadius:8,border:"1px solid #e3e8ef"}}>
                <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                  <input type="checkbox" checked={skipCard} onChange={e=>{
                    setSkipCard(e.target.checked);
                    if(e.target.checked&&cardElRef.current){
                      cardElRef.current.destroy();
                      cardElRef.current = null; // prevent double-destroy in useEffect cleanup
                      mountedRef.current = false;
                      setStripeReady(false);
                      setCardComplete(false);
                    }
                  }} style={{width:16,height:16}}/>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:NAVY}}>No card at this time</div>
                    <div style={{fontSize:11,color:"#8993a4"}}>Client will receive a payment link to add their card later</div>
                  </div>
                </label>
              </div>

              {!skipCard&&(
                <>
                  <div style={{background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#0369a1",display:"flex",gap:8,alignItems:"flex-start"}}>
                    <Icon n="lock" size={14}/><span>Card details go directly to Stripe's secure servers. We never store your full card number.</span>
                  </div>
                  <div style={{marginBottom:12}}>
                    <div style={{fontFamily:"Syne",fontWeight:700,fontSize:13,color:NAVY,marginBottom:8}}>Card Details</div>
                    {!stripeReady&&<div style={{fontSize:12,color:"#8892b0",padding:"9px 12px",border:"1px solid #cdd5ef",borderRadius:8,minHeight:42}}>Loading secure card form...</div>}
                    <div id="stripe-card-element" className={`stripe-field${cardError?" error":""}`} style={{minHeight:42,display:stripeReady?"block":"none"}}></div>
                    {cardError&&<div style={{fontSize:12,color:DANGER,marginTop:4}}>⚠️ {cardError}</div>}
                  </div>
                  <div style={{background:"#fef9c3",border:"1px solid #fde047",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#854d0e"}}>
                    💳 Client will be charged <strong>${price}/month</strong> on the 1st of each month.
                  </div>
                  <div style={{marginTop:8,fontSize:11,color:"#8892b0"}}>
                    Test cards: <code style={{background:"#f1f5f9",padding:"1px 5px",borderRadius:3}}>4242 4242 4242 4242</code> · any future date · any CVC
                  </div>
                </>
              )}
            </div>
          )}
        </div>{/* end modal-body */}

        {/* Phone: the primary action is a full-width thumb target on its own line; Cancel/Back share
            the line above it. Desktop keeps the single right-aligned row (display:contents is a
            no-op wrapper there). */}
        <div className="modal-footer" style={isMobile?{flexWrap:"wrap",flexDirection:"column",alignItems:"stretch",gap:8}:{flexWrap:"wrap"}}>
          {isMobile && !client && step===3 && !skipCard && !cardComplete && !saving && (
            <div style={{fontSize:12,color:"#8a5a06",background:"#fef6e7",border:"1px solid #f6d88a",borderRadius:8,padding:"7px 10px",lineHeight:1.4}}>
              Finish the card details above, or tick <strong>“No card at this time”</strong>, to add the client.
            </div>
          )}
          <div style={isMobile?{display:"flex",gap:8}:{display:"contents"}}>
          <button type="button" className="btn btn-ghost" onClick={onClose} style={isMobile?{flex:1,minWidth:0}:undefined}>Cancel</button>
          {!client && step>1 && <button type="button" className="btn btn-ghost" onClick={()=>setStep(s=>s-1)} style={isMobile?{flex:1,minWidth:0}:undefined}>← Back</button>}
          </div>
          {!client && step<3 && (
            <button className="btn btn-gold" style={isMobile?{width:"100%"}:undefined} onClick={()=>{
              if(step===1&&(!form.first_name||!form.last_name||!form.email||!form.zip)){alert("First name, last name, email, and zip are required");return;}
              if(step===1&&form.zip&&!getDayForZip(form.zip)){alert(`Zip ${form.zip} has no service day. Add it in Settings → Team & Service → Service Area first, then add this client.`);return;}
              if(step===2&&(!form.frequency||!form.dogs)){alert("Frequency and number of dogs are required");return;}
              setStep(s=>s+1);
            }}>Next →</button>
          )}
          {(client||step===3) && (
            <button className="btn btn-gold" style={isMobile?{width:"100%"}:undefined} onClick={handleSubmit} disabled={saving||(!client&&!skipCard&&!cardComplete)}>
              {saving?<><span className="spinner spinner-dark"/>Saving...</>:client?"Save Changes":skipCard?"✓ Add Client":"✓ Add Client & Save Card"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
